import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTicketRepository } from '../../data/hooks/useTicketRepository';
import { useActivityRepository } from '../../data/hooks/useActivityRepository';
import { TicketStatusHeader } from './components/TicketStatusHeader';
import { TicketStageTimeline } from './components/TicketStageTimeline';
import { ActivityTimeline } from './components/ActivityTimeline';
import { ReplyBox } from './components/ReplyBox';
import { ResolutionPrompt } from './components/ResolutionPrompt';
import {
  ActivityType,
  ActivityVisibility,
  AuthorType,
  getSLAStatus,
} from '../../types/ticketActivity';
import type { TicketActivity, TicketAttachment } from '../../types/ticketActivity';
import type { Ticket } from '../../types/ticket';
import { TicketStatus, WaitingReason } from '../../types/ticket';
import './UserTicketView.css';

/**
 * UserTicketView - User-facing ticket detail view
 *
 * Designed to reduce user anxiety and minimize follow-up questions:
 * - Clear status with plain language
 * - Visual stage timeline
 * - Timeline-based activity feed (newest first)
 * - Simple reply mechanism
 * - Question/answer workflow for IT questions
 * - Clear resolution and closure flow
 */
export function UserTicketView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const currentUser = useCurrentUser();
  const ticketRepo = useTicketRepository();
  const activityRepo = useActivityRepository();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [activities, setActivities] = useState<TicketActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  // Refresh activities from repository
  const refreshActivities = useCallback(async () => {
    if (!id) return;
    try {
      const publicActivities = await activityRepo.getByTicketIdAndVisibility(
        id,
        ActivityVisibility.PUBLIC
      );
      // Sort descending (newest first)
      const sorted = publicActivities.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setActivities(sorted);
    } catch (err) {
      console.error('Failed to refresh activities:', err);
    }
  }, [id, activityRepo]);

  // Load ticket and activities
  useEffect(() => {
    async function loadTicketAndActivities() {
      if (!id) {
        setError('Ticket not found');
        setIsLoading(false);
        return;
      }

      try {
        const [foundTicket, publicActivities] = await Promise.all([
          ticketRepo.getById(id),
          activityRepo.getByTicketIdAndVisibility(id, ActivityVisibility.PUBLIC),
        ]);

        if (!foundTicket) {
          setError('Ticket not found');
          setIsLoading(false);
          return;
        }

        // Verify user owns this ticket
        if (foundTicket.requesterId !== currentUser.id) {
          setError("You don't have access to this ticket");
          setIsLoading(false);
          return;
        }

        setTicket(foundTicket);

        // Sort descending (newest first)
        const sorted = publicActivities.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setActivities(sorted);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load ticket:', err);
        setError('Failed to load ticket details');
        setIsLoading(false);
      }
    }

    loadTicketAndActivities();
  }, [id, ticketRepo, activityRepo, currentUser.id]);

  // Calculate SLA status
  const slaStatus = useMemo(() => {
    if (!ticket) return null;
    return getSLAStatus(ticket.slaDeadline, ticket.status);
  }, [ticket]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/my-requests');
  }, [navigate]);

  // Handle reply submission
  const handleReplySubmit = useCallback(
    async (content: string, attachments: File[]) => {
      if (!ticket) return;

      setIsSubmittingReply(true);

      try {
        // Create attachment objects
        const attachmentObjects: TicketAttachment[] = attachments.map(
          (file, index) => ({
            id: `${ticket.id}-att-${Date.now()}-${index}`,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            url: URL.createObjectURL(file),
            uploadedAt: new Date(),
            uploadedBy: currentUser.id,
          })
        );

        // Save to activity repository
        await activityRepo.create({
          ticketId: ticket.id,
          type: ActivityType.COMMENT,
          visibility: ActivityVisibility.PUBLIC,
          authorId: currentUser.id,
          authorName: currentUser.name,
          authorType: AuthorType.USER,
          content,
          attachments: attachmentObjects,
          triggersNotification: true,
        });

        // Update ticket status if it was waiting on user
        if (ticket.status === TicketStatus.WAITING && ticket.waitingReason === WaitingReason.USER) {
          await ticketRepo.update(ticket.id, {
            status: TicketStatus.IN_PROGRESS,
          });

          // Create status change activity
          await activityRepo.create({
            ticketId: ticket.id,
            type: ActivityType.STATUS_CHANGE,
            visibility: ActivityVisibility.PUBLIC,
            authorId: 'system',
            authorName: 'System',
            authorType: AuthorType.SYSTEM,
            previousStatus: TicketStatus.WAITING,
            newStatus: TicketStatus.IN_PROGRESS,
            displayMessage: 'Response received - back in progress',
            triggersNotification: false,
          });

          // Refresh ticket data
          const updatedTicket = await ticketRepo.getById(ticket.id);
          if (updatedTicket) {
            setTicket(updatedTicket);
          }
        }

        // Refresh activities to show the new reply
        await refreshActivities();
      } catch (err) {
        console.error('Failed to submit reply:', err);
      } finally {
        setIsSubmittingReply(false);
      }
    },
    [ticket, ticketRepo, activityRepo, currentUser, refreshActivities]
  );

  // Handle answering a question from IT
  const handleAnswerQuestion = useCallback(
    async (questionId: string, answerContent: string) => {
      if (!ticket) return;

      try {
        // Create answer activity
        await activityRepo.create({
          ticketId: ticket.id,
          type: ActivityType.ANSWER_FROM_USER,
          visibility: ActivityVisibility.PUBLIC,
          authorId: currentUser.id,
          authorName: currentUser.name,
          authorType: AuthorType.USER,
          content: answerContent,
          questionActivityId: questionId,
          triggersNotification: true,
        });

        // Mark question as answered
        await activityRepo.markQuestionAnswered(questionId);

        // Update ticket status back to In Progress if it was waiting
        if (ticket.status === TicketStatus.WAITING) {
          await ticketRepo.update(ticket.id, {
            status: TicketStatus.IN_PROGRESS,
          });

          // Create status change activity
          await activityRepo.create({
            ticketId: ticket.id,
            type: ActivityType.STATUS_CHANGE,
            visibility: ActivityVisibility.PUBLIC,
            authorId: 'system',
            authorName: 'System',
            authorType: AuthorType.SYSTEM,
            previousStatus: TicketStatus.WAITING,
            newStatus: TicketStatus.IN_PROGRESS,
            displayMessage: 'Question answered - back in progress',
            triggersNotification: false,
          });

          // Refresh ticket data
          const updatedTicket = await ticketRepo.getById(ticket.id);
          if (updatedTicket) {
            setTicket(updatedTicket);
          }
        }

        // Refresh activities
        await refreshActivities();
      } catch (err) {
        console.error('Failed to submit answer:', err);
        throw err;
      }
    },
    [ticket, ticketRepo, activityRepo, currentUser, refreshActivities]
  );

  // Handle resolution confirmation
  const handleConfirmResolved = useCallback(async () => {
    if (!ticket) return;

    try {
      // Update ticket to closed
      await ticketRepo.update(ticket.id, {
        status: TicketStatus.CLOSED,
        closedAt: new Date(),
      });

      // Create closed activity
      await activityRepo.create({
        ticketId: ticket.id,
        type: ActivityType.CLOSED,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUser.id,
        authorName: currentUser.name,
        authorType: AuthorType.USER,
        displayMessage: 'User confirmed resolution',
        triggersNotification: false,
      });

      // Navigate back to requests
      navigate('/my-requests', {
        state: { ticketResolved: ticket.ticketNumber },
      });
    } catch (err) {
      console.error('Failed to confirm resolution:', err);
    }
  }, [ticket, ticketRepo, activityRepo, currentUser, navigate]);

  // Handle reopen request
  const handleReopen = useCallback(async () => {
    if (!ticket) return;

    try {
      // Update ticket status
      await ticketRepo.update(ticket.id, {
        status: TicketStatus.IN_PROGRESS,
      });

      // Create reopen activity in repository
      await activityRepo.create({
        ticketId: ticket.id,
        type: ActivityType.REOPENED,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUser.id,
        authorName: currentUser.name,
        authorType: AuthorType.USER,
        displayMessage: 'Request reopened by user',
        triggersNotification: true,
      });

      // Refresh ticket and activities
      const updatedTicket = await ticketRepo.getById(ticket.id);
      if (updatedTicket) {
        setTicket(updatedTicket);
      }
      await refreshActivities();
    } catch (err) {
      console.error('Failed to reopen ticket:', err);
    }
  }, [ticket, ticketRepo, activityRepo, currentUser, refreshActivities]);

  // Loading state
  if (isLoading) {
    return (
      <div className="user-ticket-view" data-theme={theme}>
        <div className="user-ticket-view__loading">
          <div className="user-ticket-view__spinner" />
          <span>Loading ticket details...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !ticket) {
    return (
      <div className="user-ticket-view" data-theme={theme}>
        <div className="user-ticket-view__error">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="24" cy="24" r="20" />
            <path d="M24 14v12M24 30v2" strokeLinecap="round" />
          </svg>
          <h2>{error ?? 'Ticket not found'}</h2>
          <p>We couldn't find the ticket you're looking for.</p>
          <button type="button" className="user-ticket-view__error-btn" onClick={handleBack}>
            Back to My Requests
          </button>
        </div>
      </div>
    );
  }

  const isResolved = ticket.status === TicketStatus.RESOLVED;
  const needsUserAction = ticket.status === TicketStatus.WAITING && ticket.waitingReason === WaitingReason.USER;

  return (
    <div className="user-ticket-view" data-theme={theme}>
      <div className="user-ticket-view__container">
        {/* Back navigation */}
        <button type="button" className="user-ticket-view__back" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 15l-5-5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to My Requests
        </button>

        {/* Status Header */}
        <TicketStatusHeader
          ticket={ticket}
          slaStatus={slaStatus}
          needsUserAction={needsUserAction}
        />

        {/* Stage Timeline */}
        <TicketStageTimeline ticket={ticket} />

        {/* Resolution Prompt (when resolved) */}
        {isResolved && (
          <ResolutionPrompt
            onConfirmResolved={handleConfirmResolved}
            onReopen={handleReopen}
          />
        )}

        {/* Activity Timeline */}
        <ActivityTimeline
          activities={activities}
          currentUserId={currentUser.id}
          onAnswerQuestion={handleAnswerQuestion}
        />

        {/* Reply Box (not shown for resolved tickets) */}
        {!isResolved && (
          <ReplyBox
            onSubmit={handleReplySubmit}
            isSubmitting={isSubmittingReply}
            needsUserAction={needsUserAction}
          />
        )}
      </div>
    </div>
  );
}
